import React, {
  ChangeEvent,
  FocusEvent,
  useEffect,
  useRef,
  useState
} from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { Search01Icon } from '@hugeicons/core-free-icons'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FieldError } from '@/components/ui/field'
import { ReferenceInputMethodProps } from '../types'
import { ReferenceCard, ReferenceLoadingCard } from '../ReferenceCard'

interface GetInputProps<T extends object> extends ReferenceInputMethodProps<T> {
  getById: (id: string) => Promise<T | undefined>
  icon?: React.ReactNode | undefined
  placeholder?: string | undefined
}

export default function GetInput<T extends object>({
  error,
  getById,
  inputId,
  onAdd,
  onBlur,
  onFocus,
  required,
  RenderResource,
  onRemove,
  resource,
  isLoading,
  icon,
  placeholder
}: GetInputProps<T>) {
  const buttonRef = useRef<HTMLButtonElement>(null)

  const [inputValue, setInputValue] = useState<string>('')
  const [privateError, setPrivateError] = useState<string>()
  const [queryLoading, setQueryLoading] = useState<boolean>(false)

  useEffect(() => {
    if (privateError) {
      const timeout = setTimeout(() => setPrivateError(undefined), 5000)

      return () => clearTimeout(timeout)
    }
  }, [privateError])

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    setInputValue(event.target.value)
  }

  function onSearch() {
    setQueryLoading(true)
    getById(inputValue)
      .then(result => {
        if (!result) throw new Error('No result found')
        onAdd(result)
      })
      .catch((e: Error) => setPrivateError(e.message))
      .finally(() => setQueryLoading(false))
  }

  function handleBlur(e: FocusEvent<HTMLInputElement>) {
    // Don't trigger the onBlur if the search button is clicked
    if (e.relatedTarget === buttonRef.current) return
    onBlur()
    onSearch()
  }

  if (isLoading || queryLoading) {
    return (
      <>
        <ReferenceLoadingCard icon={icon} />
        <FieldError>{privateError ?? error}</FieldError>
      </>
    )
  }

  if (resource) {
    return (
      <>
        <ReferenceCard icon={icon} onRemove={onRemove}>
          <RenderResource resource={resource} inputValue={inputValue} />
        </ReferenceCard>
        <FieldError>{privateError ?? error}</FieldError>
      </>
    )
  }

  return (
    <>
      <div className='mb-1 flex'>
        <div className='relative flex-1'>
          {icon && (
            <div className='absolute left-2 top-1/2 -translate-y-1/2'>
              {icon}
            </div>
          )}
          <Input
            name={inputId}
            type='text'
            value={inputValue}
            placeholder={placeholder}
            required={required}
            aria-invalid={!!error}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={onFocus}
            className={cn('rounded-r-none', icon && 'pl-8')}
          />
        </div>
        <Button
          type='button'
          size='icon-lg'
          ref={buttonRef}
          onClick={onSearch}
          aria-label='search'
          className='border-primary rounded-l-none'
        >
          <HugeiconsIcon icon={Search01Icon} strokeWidth={2} />
        </Button>
      </div>

      <FieldError>{privateError ?? error}</FieldError>
    </>
  )
}
